import store from '../store';
import {
  DialogProgrammatic as Dialog,
  SnackbarProgrammatic as Snackbar,
  ToastProgrammatic as Toast
} from 'buefy';
import {modelIs} from "../filters";
import {ENUMS} from "./enums";
import {EventBus} from "../event-bus";
import dates from "./dates";

const utils = {
  // Haven't looked into
  // This will fully clone, but not arrays or objects because they are pass by value, and the value is a pointer.
  clone(obj) {
    return JSON.parse(JSON.stringify(obj));
  },
  getCookie(cookieName) {
    let name = cookieName + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }

    return null;
  },
  showDeleteDialog(label, callback, options = {}) {
    const title = options.title ? options.title : `Delete ${label}`;
    const message = options.message ? options.message : `Are you sure you want to <strong>delete</strong> ${label}?`;
    const confirmText = options.confirmText ? options.confirmText : 'Delete';
    const type = options.type ? options.type : 'is-danger';

    Dialog.confirm({
      title,
      message,
      confirmText,
      type,
      hasIcon: true,
      onConfirm() {
        if (callback) callback();
      }
    });
  },
  getAssignedUsers(record) {
    const users = [];
    const owners = record.users.map(u => u.id);
    const tasks = store.getters.pipelineIncompleteTasks.filter(a => a.record_id === record.id);
    const appointments = store.getters.pipelineAppointments.filter(a => a.record_id === record.id);

    if (record.user_id) {
      users.push(record.user_id);
    }

    for (const task of tasks) {
      if (task.assigned_to_id && !users.includes(task.assigned_to_id) && !owners.includes(task.assigned_to_id)) {
        users.push(task.assigned_to_id);
      }
    }

    for (const app of appointments) {
      for (const attendee of app.attendees) {
        if (attendee.class_name === 'App\\Models\\User') {
          if (!users.includes(attendee.id) && !owners.includes(attendee.id)) {
            users.push(attendee.id);
          }
        }
      }
    }

    return users;
  },
  modelIs(model, expectedClassName) {
    return modelIs(expectedClassName, model);
  },
  generateClientId() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
  },
  concatStrings() {
    // Set a starting total
    let string = '';

    // Add each number to the total
    for (let i = 0; i < arguments.length; i++) {
      // The or empty string allows you to concat null or undefined without having weird side effects.
      string += arguments[i] || '';
    }

    // Return to the total
    return string;
  },
  camelCase(string) {
    return string.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    }).replace(/\s+/g, '');
  },
  stringIsEmpty(string) {
    if (string) {
      return !string.replace(/\s/g, '').length;
    }

    return true;
  },
  truncateString(text, maxLength = 0, defaultForEmpty = '--') {
    if (!text) {
      return defaultForEmpty;
    }

    if (this.stringIsEmpty(text)) {
      return defaultForEmpty;
    }

    if (maxLength === 0) {
      return text;
    }

    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }

    return text;
  },
  newLineString(text) {
    return text.replace(/\\n/g, "<br>");
  },
  validateEmail(email) {
    let emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  },
  getRecordsPreviewTitle(record) {
    const address = record.property.line_1 ? record.property.line_1 : 'Unknown Address';

    if (record.contact) {
      const name = `${record.contact.first_name === null ? '' : record.contact.first_name} ${record.contact.last_name === null ? '' : record.contact.last_name}`;
      const contact = name === ' ' ? 'Unknown Contact' : name;

      return `${address} | ${contact}`;
    }

    return `${address}`;
  },
  getRecordAddressLabel(record) {
    const line_1 = record.property.line_1;
    const city = record.property.city;
    const state = record.property.state;

    if (line_1 && city && state) {
      return line_1 + ', ' + city + ' ' + state;
    }

    if (line_1 && city) {
      return line_1 + ', ' + city;
    }

    if (line_1 && state) {
      return line_1 + ', ' + state;
    }

    if (line_1) {
      return line_1;
    }

    return 'Unknown Street';
  },
  formatAddressToLine1AndLine2(address) {
    const street = address.line_1;
    const city = address.city;
    const state = address.state;
    const postcode = address.postcode;

    // If line_1 is null skip everything and return Unknown Address
    if (!street) {
      return {
        line_1: "Unknown Address",
        line_2: null
      };
    }

    // Set line_2 to null by default
    // then set line_2 based on what city, state, or postcode info is available
    let line_2 = null;

    // All three
    if (city && state && postcode) line_2 = `${city}, ${state} ${postcode}`;

    // No city
    else if (!city && state && postcode) line_2 = `${state} ${postcode}`;
    // No state
    else if (city && !state && postcode) line_2 = `${city}, ${postcode}`;
    // No postcode
    else if (city && state && !postcode) line_2 = `${city}, ${state}`;

    // Only city
    else if (city && !state && !postcode) line_2 = city;
    // Only State
    else if (!city && state && !postcode) line_2 = state;
    // Only Postcode
    else if (!city && !state && postcode) line_2 = postcode;

    return {
      line_1: street,
      line_2: line_2
    };
  },
  getBillingDetails(name) {
    const account = store.getters.account;

    return {
      name,
      email: store.state.activeUser.email_address,
      phone: store.state.activeUser.notifications_phone_number,
      address: {
        city: account.city,
        line1: account.street_address,
        postal_code: account.postcode,
        state: account.state,
      }
    };
  },
  copyToClipboard(content) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content).then(() => {
        Toast.open({
          message: 'Copied to clipboard',
          type: 'is-success'
        });
      }, () => {
        Snackbar.open({message: 'Unable to copy', type: 'is-danger'});
      });
    } else {
      Snackbar.open({message: 'Unable to copy', type: 'is-danger'});
    }
  },
  getStateByZip(zipcode) {
    //Ensure param is a string to prevent unpredictable parsing results
    if (typeof zipcode !== 'string') {
      return null;
    }

    // Ensure we have exactly 5 characters to parse
    if (zipcode.length !== 5) {
      return null;
    }

    // Ensure we don't parse strings starting with 0 as octal values
    const thiszip = parseInt(zipcode, 10);

    let st = '';
    let state = '';

    // Code blocks alphabetized by state
    if (thiszip >= 35000 && thiszip <= 36999) {
      st = 'AL';
      state = 'Alabama';
    } else if (thiszip >= 99500 && thiszip <= 99999) {
      st = 'AK';
      state = 'Alaska';
    } else if (thiszip >= 85000 && thiszip <= 86999) {
      st = 'AZ';
      state = 'Arizona';
    } else if (thiszip >= 71600 && thiszip <= 72999) {
      st = 'AR';
      state = 'Arkansas';
    } else if (thiszip >= 90000 && thiszip <= 96699) {
      st = 'CA';
      state = 'California';
    } else if (thiszip >= 80000 && thiszip <= 81999) {
      st = 'CO';
      state = 'Colorado';
    } else if (thiszip >= 6000 && thiszip <= 6999) {
      st = 'CT';
      state = 'Connecticut';
    } else if (thiszip >= 19700 && thiszip <= 19999) {
      st = 'DE';
      state = 'Delaware';
    } else if (thiszip >= 32000 && thiszip <= 34999) {
      st = 'FL';
      state = 'Florida';
    } else if (thiszip >= 30000 && thiszip <= 31999) {
      st = 'GA';
      state = 'Georgia';
    } else if (thiszip >= 96700 && thiszip <= 96999) {
      st = 'HI';
      state = 'Hawaii';
    } else if (thiszip >= 83200 && thiszip <= 83999) {
      st = 'ID';
      state = 'Idaho';
    } else if (thiszip >= 60000 && thiszip <= 62999) {
      st = 'IL';
      state = 'Illinois';
    } else if (thiszip >= 46000 && thiszip <= 47999) {
      st = 'IN';
      state = 'Indiana';
    } else if (thiszip >= 50000 && thiszip <= 52999) {
      st = 'IA';
      state = 'Iowa';
    } else if (thiszip >= 66000 && thiszip <= 67999) {
      st = 'KS';
      state = 'Kansas';
    } else if (thiszip >= 40000 && thiszip <= 42999) {
      st = 'KY';
      state = 'Kentucky';
    } else if (thiszip >= 70000 && thiszip <= 71599) {
      st = 'LA';
      state = 'Louisiana';
    } else if (thiszip >= 3900 && thiszip <= 4999) {
      st = 'ME';
      state = 'Maine';
    } else if (thiszip >= 20600 && thiszip <= 21999) {
      st = 'MD';
      state = 'Maryland';
    } else if (thiszip >= 1000 && thiszip <= 2799) {
      st = 'MA';
      state = 'Massachusetts';
    } else if (thiszip >= 48000 && thiszip <= 49999) {
      st = 'MI';
      state = 'Michigan';
    } else if (thiszip >= 55000 && thiszip <= 56999) {
      st = 'MN';
      state = 'Minnesota';
    } else if (thiszip >= 38600 && thiszip <= 39999) {
      st = 'MS';
      state = 'Mississippi';
    } else if (thiszip >= 63000 && thiszip <= 65999) {
      st = 'MO';
      state = 'Missouri';
    } else if (thiszip >= 59000 && thiszip <= 59999) {
      st = 'MT';
      state = 'Montana';
    } else if (thiszip >= 27000 && thiszip <= 28999) {
      st = 'NC';
      state = 'North Carolina';
    } else if (thiszip >= 58000 && thiszip <= 58999) {
      st = 'ND';
      state = 'North Dakota';
    } else if (thiszip >= 68000 && thiszip <= 69999) {
      st = 'NE';
      state = 'Nebraska';
    } else if (thiszip >= 88900 && thiszip <= 89999) {
      st = 'NV';
      state = 'Nevada';
    } else if (thiszip >= 3000 && thiszip <= 3899) {
      st = 'NH';
      state = 'New Hampshire';
    } else if (thiszip >= 7000 && thiszip <= 8999) {
      st = 'NJ';
      state = 'New Jersey';
    } else if (thiszip >= 87000 && thiszip <= 88499) {
      st = 'NM';
      state = 'New Mexico';
    } else if (thiszip >= 10000 && thiszip <= 14999) {
      st = 'NY';
      state = 'New York';
    } else if (thiszip >= 43000 && thiszip <= 45999) {
      st = 'OH';
      state = 'Ohio';
    } else if (thiszip >= 73000 && thiszip <= 74999) {
      st = 'OK';
      state = 'Oklahoma';
    } else if (thiszip >= 97000 && thiszip <= 97999) {
      st = 'OR';
      state = 'Oregon';
    } else if (thiszip >= 15000 && thiszip <= 19699) {
      st = 'PA';
      state = 'Pennsylvania';
    } else if (thiszip >= 300 && thiszip <= 999) {
      st = 'PR';
      state = 'Puerto Rico';
    } else if (thiszip >= 2800 && thiszip <= 2999) {
      st = 'RI';
      state = 'Rhode Island';
    } else if (thiszip >= 29000 && thiszip <= 29999) {
      st = 'SC';
      state = 'South Carolina';
    } else if (thiszip >= 57000 && thiszip <= 57999) {
      st = 'SD';
      state = 'South Dakota';
    } else if (thiszip >= 37000 && thiszip <= 38599) {
      st = 'TN';
      state = 'Tennessee';
    } else if ((thiszip >= 75000 && thiszip <= 79999) || (thiszip >= 88500 && thiszip <= 88599)) {
      st = 'TX';
      state = 'Texas';
    } else if (thiszip >= 84000 && thiszip <= 84999) {
      st = 'UT';
      state = 'Utah';
    } else if (thiszip >= 5000 && thiszip <= 5999) {
      st = 'VT';
      state = 'Vermont';
    } else if (thiszip >= 22000 && thiszip <= 24699) {
      st = 'VA';
      state = 'Virginia';
    } else if (thiszip >= 20000 && thiszip <= 20599) {
      st = 'DC';
      state = 'District Of Columbia';
    } else if (thiszip >= 98000 && thiszip <= 99499) {
      st = 'WA';
      state = 'Washington';
    } else if (thiszip >= 24700 && thiszip <= 26999) {
      st = 'WV';
      state = 'West Virginia';
    } else if (thiszip >= 53000 && thiszip <= 54999) {
      st = 'WI';
      state = 'Wisconsin';
    } else if (thiszip >= 82000 && thiszip <= 83199) {
      st = 'WY';
      state = 'Wyoming';
    } else {
      st = 'none';
      state = 'none';
    }

    return {state, st};
  },
  getRouterHash(route, key) {
    let recordHash = route.hash.substr(1).split('&').filter(function (pair) {
      return decodeURIComponent(pair.split('=', 2)[0]) === key;
    });

    if (recordHash.length) {
      return decodeURIComponent(recordHash[0].split('=', 2)[1]);
    } else {
      return null;
    }
  },

  // Want to remove
  getEnumLabelDeprecated(prop, val) {
    const obj = store.state.enums[prop].find(e => e.value === val);

    if (obj) {
      return obj.label;
    }

    return val;
  },
  getCampaignIcon(campaignId) {
    const camp = store.state.market.campaigns.find(c => c.id === campaignId);

    if (camp) {
      switch (this.newGetEnumLabel(camp.channel, ENUMS.CampaignChannelEnum)) {
        case 'Google Ads':
          return 'campaign-google-ads';
        case 'Facebook Ads':
          return 'campaign-facebook';
        case 'Website':
          return 'campaign-website';
        case 'Direct Mail':
          return 'campaign-direct-mail';
        case 'TV':
          return 'campaign-tv';
        case 'Radio':
          return 'campaign-radio';
        case 'Billboards':
          return 'campaign-billboards';
        case 'Bandit Signs':
          return 'campaign-bandit-signs';
        case 'Door Knocking':
          return 'campaign-door-knocking';
        case 'Cold Calling':
          return 'campaign-cold-calling';
        case 'Classifieds':
          return 'campaign-classifieds';
        case 'Organic':
          return 'campaign-organic';
        case 'Referrals':
          return 'campaign-referrals';
        case 'MLS':
          return 'campaign-mls';
        case 'Texting':
          return 'campaign-texting';
        case 'Ringless Voicemail':
          return 'campaign-ringless-voicemail';
        case 'Other':
          return 'campaign-other';
        default:
          return 'campaign';
      }
    } else {
      return 'campaign';
    }
  },

  // Want to Keep
  mockApi(data, timeOut = 500) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve({
          data: data
        });
      }, timeOut);
    });
  },
  newGetEnumLabel(value, Enum, emptyString = 'Unknown') {
    let results = Object.entries(Enum).find(([label, data]) => {
      return data.value === value;
    });

    return (results) ? results[1].label : emptyString;
  },
  findEnumByValue(value, Enum) {
    let results = Object.entries(Enum).find(([label, data]) => {
      return data.value === value;
    });

    return (results) ? results[1].label : null;
  },
  getEnumItem(value, Enum) {
    let results = Object.entries(Enum).find(([label, data]) => {
      return data.value === value;
    });

    return (results) ? results[1] : null;
  },
  getEnumLabel(prop, value) {
    const obj = ENUMS[prop].find(e => e.value === value);

    if (obj) {
      return obj.label;
    }

    return val;
  },

  playNotificationSound(notificationSound) {
    let file = null

    if(notificationSound === ENUMS.NotificationSoundEnum.Short.value) {
      file = '/audio/correct-answer-tone.mp3'
    }
    if(notificationSound === ENUMS.NotificationSoundEnum.Medium.value) {
      file = '/audio/positive-notification.mp3'
    }
    if(notificationSound === ENUMS.NotificationSoundEnum.Long.value) {
      file = '/audio/happy-bells-notification.mp3'
    }

    if(file) {
      const audio = new Audio(file);
      audio.play();
    }
  },

  formatDuration(duration) {
    if (!duration) return '00:00:00';

    const pad = (num, size) => {
      return ("000" + num).slice(size * -1);
    };

    const time = parseFloat(duration).toFixed(3);

    const hours = Math.floor(time / 60 / 60);
    const minutes = Math.floor(time / 60) % 60;
    const seconds = Math.floor(time - minutes * 60);

    return pad(hours, 2) + ":" + pad(minutes, 2) + ":" + pad(seconds, 2);
  },
  formatCurrency(value, decimals = 2) {
    value = parseFloat(value);

    if (typeof value == 'number') {
      return ('$' + value.toFixed(decimals).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
    }

    return value;
  },
  formatDigit(value, decimals = 0) {
    value = parseFloat(value);

    if (typeof value == 'number') {
      return (value.toFixed(decimals).replace(/\d(?=(\d{3})+\.)/g, '$&,'));
    }

    return value;
  },
  formatNumber(value) {
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return [
        '(',
        match[2],
        ') ',
        match[3],
        '-',
        match[4]
      ].join('');
    }

    return value;
  },
  formatNumberWithComma(value, decimals = 0) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  },
  async confirm(message, title = 'Are you sure?', handle) {
    return await new Promise((resolve, reject) => {
      handle = Dialog.confirm({
        title: title,
        message: message,
        onCancel: () => reject(false),
        onConfirm: () => resolve(true)
      });
    });
  },

  async deleteConfirm(message, title = 'Are you sure?', confirmText = 'Delete') {
    return await new Promise((resolve, reject) => {
      Dialog.confirm({
        title: title,
        message: message,
        confirmText: confirmText,
        type: "is-danger",
        hasIcon: true,
        onCancel: () => reject(false),
        onConfirm: () => resolve(true)
      });
    });
  },

  closeRecordModal() {
    EventBus.$emit('closeLeadModal');
  },
  closeContactModal() {
    EventBus.$emit('closeContactModal');
  },
  closeAccountModal() {
    EventBus.$emit('closeAccountModal');
  },
  closeCommEmailModal() {
    EventBus.$emit('closeCommEmailModal');
  },
  userDisplayName(userId) {
    let user = store.getters.userById(userId);
    if (!user) {
      return '--';
    }

    let displayName = utils.concatStrings(user.first_name, ' ', user.last_name);
    return utils.stringIsEmpty(displayName) ? '--' : displayName;
  },

  displayFullName(fullName, unknown = 'Unknown') {
    return fullName !== ' ' ? fullName : unknown;
  },
  stripTags(string) {
    // dont fail when string is null
    return (string) ? string.replace(/<[^>]*>?/gm, '') : string;
  },

  debounce(later, ref, wait = 500) {
    clearTimeout(ref);
    return setTimeout(later, wait);
  },
  processDownload(response, filename) {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  },

  /**
   * Date convert functions
   *
   * Leaving these functions as they are since they don't involve moment
   */
  dateToTimeStringWithoutTimezone(date) {
    let hour = date.getHours();
    let minute = date.getMinutes();
    // let second = date.getSeconds();

    let time = ((hour < 10) ? '0' + hour : hour);
    time += ((minute < 10) ? ':0' : ':') + minute;
    // time += ((second < 10) ? ':0' : ':') + second;

    return time;
  },
  timeStringWithoutTimezoneToDate(time) {
    if (time === undefined) return;

    let [hours, minutes, seconds] = time.split(':').map(str => Number.parseInt(str));

    let date = new Date();
    date.setHours(hours || 0);
    date.setMinutes(minutes || 0);
    date.setSeconds(seconds || 0);

    return date;
  },

  // TODO this is used on comms page which has some major issues that need to be addressed later
  groupIntoDays(items, timeString, isDescending) {
    let daysArray = [];

    if (isDescending) {
      items = items.sort((a, b) => {
        return new Date(b[timeString]) - new Date(a[timeString]);
      });
    } else {
      items = items.sort((a, b) => {
        return new Date(a[timeString]) - new Date(b[timeString]);
      });
    }

    items.forEach(item => {
      let newDay = {
        month: dates.formatAsMonthLong(item[timeString]),
        date: dates.formatAsDay(item[timeString]),
        year: dates.formatAsYear(item[timeString]),
        items: []
      };

      if (!daysArray.find(day => day.month === newDay.month && day.date === newDay.date && newDay.year === day.year)) {
        daysArray.push(newDay);
      }
    });

    daysArray.map(day => {
      day.items = items.filter(item => {
        let month = dates.formatAsMonthLong(item[timeString]);
        let date = dates.formatAsDay(item[timeString]);
        let year = dates.formatAsYear(item[timeString]);

        return day.month === month && day.date === date && day.year === year;
      });
    });

    return daysArray;
  },
  getHotMotivatorsFromRealPropertyData(property) {
    if (property.real_estate_property_data && property.real_estate_property_data_errored_at === null) {
      return {
        'vacant': property.real_estate_property_data.vacant ?? false,
        'preForeclosure': property.real_estate_property_data.preForeclosure ?? false,
        'quitClaim': property.real_estate_property_data.quitClaim ?? false,
        'trusteeSale': property.real_estate_property_data.trusteeSale ?? false,
        'inherited': property.real_estate_property_data.inherited ?? false,
        'death': property.real_estate_property_data.death ?? false,
        'deathTransfer': property.real_estate_property_data.deathTransfer ?? false,
        'warrantyDeed': property.real_estate_property_data.warrantyDeed ?? false,
        'lien': property.real_estate_property_data.lien ?? false,
        'taxLien': property.real_estate_property_data.taxLien ?? false,
        'deedInLieu': property.real_estate_property_data.deedInLieu ?? false,
        'spousalDeath': property.real_estate_property_data.spousalDeath ?? false,
        'adjustableRate': property.real_estate_property_data.adjustableRate ?? false,
        'mlsCancelled': property.real_estate_property_data.mlsCancelled ?? false,
        'mlsFailed': property.real_estate_property_data.mlsFailed ?? false,
      };
    }

    return {
      'vacant': false,
      'preForeclosure': false,
      'quitClaim': false,
      'trusteeSale': false,
      'inherited': false,
      'death': false,
      'deathTransfer': false,
      'warrantyDeed': false,
      'lien': false,
      'taxLien': false,
      'deedInLieu': false,
      'spousalDeath': false,
      'adjustableRate': false,
      'mlsCancelled': false,
      'mlsFailed': false,
    };
  }
};

export default utils;
